import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast, Toaster } from "sonner";
import {
  ArrowRight, Phone, ShieldCheck, RotateCcw, ChevronLeft, Hash, User,
  Megaphone, LifeBuoy, Radar, TrendingUp, Wallet, Target, PiggyBank, MessageCircle,
} from "lucide-react";
import Brand from "../../components/Brand";

const WELCOME_HERO = "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?w=1200&q=80&auto=format&fit=crop";

const CAPABILITIES = [
  { icon: Megaphone, label: "Run Campaigns for Solar Installations" },
  { icon: LifeBuoy, label: "Raise Service Requests — New & Existing Customers" },
  { icon: Radar, label: "Track Real-Time Status of Installations & Service" },
  { icon: TrendingUp, label: "Maximize Earnings through Installations" },
  { icon: Wallet, label: "Track Payouts against Installations" },
  { icon: Target, label: "Follow Target vs Achievement" },
  { icon: PiggyBank, label: "Estimate Savings through Solar Installations" },
  { icon: MessageCircle, label: "Address Customer Queries" },
];

function normalisePhone(raw) {
  const p = (raw || "").trim().replace(/[\s\-()]/g, "");
  if (!p) return "";
  if (p.startsWith("+")) return p;
  const digits = p.replace(/\D/g, "");
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
  return digits ? `+${digits}` : "";
}

const STEP_META = {
  phone: { n: 1, label: "Phone" },
  otp: { n: 2, label: "Verify OTP" },
  details: { n: 3, label: "Your Details" },
};

export default function ScSignup() {
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [step, setStep] = useState("phone");
  const [isNew, setIsNew] = useState(null);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [welcomeName, setWelcomeName] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const e164 = useMemo(() => normalisePhone(phone), [phone]);
  const phoneValid = e164.length >= 12 && e164.length <= 15;
  const totalSteps = isNew === false ? 2 : 3;

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  async function requestOtp() {
    if (!phoneValid) { toast.error("Enter a valid phone number"); return; }
    setSending(true);
    try {
      const r = await api.post("/sc-auth/otp/request", { phone: e164 });
      setDelivery(r.data?.delivery || "sms");
      setSecondsLeft(r.data?.expires_in_seconds || 300);
      setIsNew(r.data?.is_new ?? null);
      setStep("otp");
      toast.success(r.data?.delivery === "sms" ? "OTP sent via SMS" : "OTP generated (mock mode)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  }

  async function finishVerify(extra) {
    const { data } = await api.post("/sc-auth/otp/verify", { phone: e164, code: code.trim(), ...extra });
    await refresh?.();
    setWelcomeName(data?.name || extra.name || "");
    setStep("welcome");
  }

  // Step 2 submit: returning SCs are verified right here and skip straight to
  // Welcome; new SCs just pass the format check and move on to Step 3 — their
  // OTP is only actually checked server-side when that step submits, since
  // user-creation and OTP-verification happen in the one atomic call.
  async function continueFromOtp(e) {
    e?.preventDefault();
    if (code.trim().length < 4) { toast.error("Enter the 6-digit OTP"); return; }
    if (isNew === false) {
      setVerifying(true);
      try {
        await finishVerify({});
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally { setVerifying(false); }
    } else {
      setStep("details");
    }
  }

  async function completeSignup(e) {
    e.preventDefault();
    if (!name.trim()) { toast.error("Enter your name"); return; }
    if (!/^\d{6}$/.test(pinCode)) { toast.error("Enter a valid 6-digit PIN code"); return; }
    setVerifying(true);
    try {
      await finishVerify({ name: name.trim(), pin_code: pinCode });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setVerifying(false); }
  }

  if (step === "welcome") {
    return (
      <div className="min-h-screen bg-[#FFFCF7] text-[#0F2A14]" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }} data-testid="sc-signup-step-welcome">
        <Toaster position="top-center" richColors />
        <div className="relative max-w-md mx-auto min-h-screen flex flex-col bg-white shadow-sm">
          <div className="relative h-56 flex-shrink-0 overflow-hidden">
            <img src={WELCOME_HERO} alt="Large solar installation across fields" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A10] via-[#0B2A10]/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 px-6 pb-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-200">Purvodaya Solar Consultant</div>
              <h1 className="text-3xl font-black text-white leading-tight mt-1" data-testid="sc-welcome-heading">
                Welcome, {welcomeName || "there"}!
              </h1>
            </div>
          </div>

          <div className="flex-1 px-6 pt-5 pb-8 flex flex-col">
            <p className="text-sm text-[#334155] leading-relaxed" data-testid="sc-welcome-tagline">
              You've just joined India's solar revolution — every rooftop you power cuts bills, cuts carbon,
              and grows your own earnings. Here's everything you can do, all in one place:
            </p>

            <div className="mt-5 grid grid-cols-2 gap-2.5 flex-1">
              {CAPABILITIES.map(({ icon: Icon, label }, i) => (
                <div key={i} data-testid={`sc-welcome-capability-${i}`} className="bg-[#F4FBF2] border border-[#DCEEDC] p-3 flex flex-col gap-2">
                  <Icon className="w-4 h-4 text-[#1B5E20]" />
                  <div className="text-[11px] font-semibold leading-snug text-[#0F2A14]">{label}</div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => nav("/sc", { replace: true })}
              data-testid="sc-welcome-continue-btn"
              className="mt-6 w-full bg-[#1B5E20] text-white py-4 font-mono text-sm uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2.5 active:scale-[0.98] transition-transform hover:bg-[#0F3D14]"
            >
              Enter the App <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFCF7] text-[#0F2A14] relative overflow-hidden" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
      <Toaster position="top-center" richColors />
      {/* Soft warm accents */}
      <div className="pointer-events-none absolute -top-32 -right-24 w-80 h-80 rounded-full bg-[#FFF4E5] blur-3xl"></div>
      <div className="pointer-events-none absolute top-1/3 -left-32 w-72 h-72 rounded-full bg-[#E8F5E9] blur-3xl"></div>

      <div className="relative max-w-md mx-auto min-h-screen flex flex-col px-6 pt-8 pb-8 bg-white shadow-sm">
        <div className="flex items-center gap-3">
          <Brand variant="full" to="/sc/signup" size={40} />
        </div>

        <div className="mt-7 flex items-center gap-2">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} className={"h-1 flex-1 rounded-full " + (STEP_META[step].n >= i + 1 ? "bg-[#F39C12]" : "bg-[#E8E2D6]")}></div>
          ))}
        </div>
        <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">
          Step {STEP_META[step].n} of {totalSteps} — {STEP_META[step].label}
        </div>

        <h1 className="text-4xl font-black leading-[1.05] mt-5">
          {step === "phone" && <>Sign in to <span className="italic font-bold text-[#F39C12]">Purvodaya.</span></>}
          {step === "otp" && <>Verify it's <span className="italic font-bold text-[#F39C12]">you.</span></>}
          {step === "details" && <>Tell us <span className="italic font-bold text-[#F39C12]">about you.</span></>}
        </h1>
        <p className="mt-3 text-sm text-[#475569] leading-relaxed">
          {step === "phone" && "Enter your mobile number — new SCs get auto-onboarded, returning SCs are logged straight in. No password needed."}
          {step === "otp" && <>OTP sent to <span className="text-[#0F2A14] font-mono">{e164}</span>. {delivery === "mock" ? "Dev mode: ask admin to fetch from backend logs." : "Check your SMS inbox."}</>}
          {step === "details" && "Just this once — your name and PIN code set the Region we'll assign you to."}
        </p>

        <div className="mt-8 flex-1">
          {step === "phone" && (
            <form onSubmit={(e) => { e.preventDefault(); requestOtp(); }} className="space-y-5" data-testid="sc-signup-step-phone">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-[#475569] mb-2">Mobile number</label>
                <div className="flex items-center gap-2 bg-white border-2 border-[#E8E2D6] focus-within:border-[#F39C12] px-4 py-3.5 transition-colors">
                  <Phone className="w-4 h-4 text-[#1B5E20] flex-shrink-0" />
                  <span className="font-mono text-sm text-[#94A3B8]">+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    data-testid="sc-signup-phone"
                    placeholder="98765 43210"
                    className="flex-1 bg-transparent outline-none text-lg font-mono tracking-wider placeholder-[#94A3B8] text-[#0F2A14]"
                  />
                </div>
                {phone && e164 && (
                  <div className="font-mono text-[10px] text-[#94A3B8] mt-1.5">→ <span className="text-[#1B5E20] font-medium">{e164}</span></div>
                )}
              </div>

              <button
                type="submit"
                disabled={!phoneValid || sending}
                data-testid="sc-signup-send-otp"
                className="w-full bg-[#1B5E20] text-white py-4 font-mono text-sm uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-transform hover:bg-[#0F3D14]"
              >
                {sending ? "Sending OTP…" : "Send OTP"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>

              <p className="text-[10px] font-mono text-[#94A3B8] leading-relaxed">
                By continuing you agree to receive transactional SMS from Purvodaya Solar. Standard carrier rates may apply.
              </p>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={continueFromOtp} className="space-y-5" data-testid="sc-signup-step-otp">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-[#475569] mb-2">6-digit OTP</label>
                <div className="flex items-center gap-2 bg-white border-2 border-[#E8E2D6] focus-within:border-[#F39C12] px-4 py-3 transition-colors">
                  <ShieldCheck className="w-4 h-4 text-[#1B5E20] flex-shrink-0" />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="sc-signup-otp"
                    placeholder="••••••"
                    className="flex-1 bg-transparent outline-none text-2xl tracking-[0.6em] font-mono text-center text-[#0F2A14] placeholder-[#94A3B8]"
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="font-mono text-[10px] text-[#475569]">
                    {secondsLeft > 0 ? `Expires in ${Math.floor(secondsLeft / 60)}m ${(secondsLeft % 60).toString().padStart(2, "0")}s` : "OTP expired"}
                  </div>
                  <button
                    type="button"
                    onClick={requestOtp}
                    disabled={sending || secondsLeft > 250}
                    data-testid="sc-signup-resend-otp"
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F39C12] disabled:opacity-30 inline-flex items-center gap-1 hover:text-[#0F3D14]"
                  >
                    <RotateCcw className="w-3 h-3" /> Resend
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={verifying || code.length < 4}
                data-testid="sc-signup-verify-otp"
                className="w-full bg-[#1B5E20] text-white py-4 font-mono text-sm uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-transform hover:bg-[#0F3D14]"
              >
                {verifying ? "Verifying…" : isNew === false ? "Verify & Continue" : "Verify & Next"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>

              <button
                type="button"
                onClick={() => { setStep("phone"); setCode(""); }}
                data-testid="sc-signup-change-phone"
                className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] text-[#475569] hover:text-[#0F2A14]"
              >
                <ChevronLeft className="w-3 h-3" /> Change phone number
              </button>
            </form>
          )}

          {step === "details" && (
            <form onSubmit={completeSignup} className="space-y-5" data-testid="sc-signup-step-details">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-[#475569] mb-2">Your name</label>
                <div className="flex items-center gap-2 bg-white border-2 border-[#E8E2D6] focus-within:border-[#F39C12] px-4 py-3.5 transition-colors">
                  <User className="w-4 h-4 text-[#1B5E20] flex-shrink-0" />
                  <input
                    type="text"
                    required
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="sc-signup-name"
                    placeholder="e.g. Yogesh Patil"
                    className="flex-1 bg-transparent outline-none text-base text-[#0F2A14] placeholder-[#94A3B8]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-[#475569] mb-2">PIN code</label>
                <div className="flex items-center gap-2 bg-white border-2 border-[#E8E2D6] focus-within:border-[#F39C12] px-4 py-3.5 transition-colors">
                  <Hash className="w-4 h-4 text-[#1B5E20] flex-shrink-0" />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    value={pinCode}
                    onChange={(e) => setPinCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    data-testid="sc-signup-pincode"
                    placeholder="e.g. 411001"
                    className="flex-1 bg-transparent outline-none text-lg font-mono tracking-wider text-[#0F2A14] placeholder-[#94A3B8]"
                  />
                </div>
                <p className="font-mono text-[10px] text-[#94A3B8] mt-1.5">Your Region is the Permissible Territory this PIN code belongs to.</p>
              </div>

              <button
                type="submit"
                disabled={verifying || !name.trim() || pinCode.length < 6}
                data-testid="sc-signup-complete"
                className="w-full bg-[#1B5E20] text-white py-4 font-mono text-sm uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-transform hover:bg-[#0F3D14]"
              >
                {verifying ? "Setting up…" : "Finish Signing Up"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>

              <button
                type="button"
                onClick={() => setStep("otp")}
                data-testid="sc-signup-back-to-otp"
                className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] text-[#475569] hover:text-[#0F2A14]"
              >
                <ChevronLeft className="w-3 h-3" /> Back
              </button>
            </form>
          )}
        </div>

        <div className="pt-6 mt-6 border-t border-[#E8E2D6]">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">
            CRM staff?{" "}
            <Link to="/login" data-testid="sc-signup-staff-login" className="text-[#1B5E20] underline decoration-[#F39C12] underline-offset-2 font-semibold">
              Email + password sign-in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
